import katex from "katex";
import "katex/dist/katex.min.css";

/**
 * Common LaTeX macros for standard mathematical & scientific notation
 */
const KATEX_MACROS = {
  "\\degree": "^{\\circ}",
  "\\textdegree": "^{\\circ}",
  "\\celsius": "^{\\circ}\\text{C}",
  "\\celcius": "^{\\circ}\\text{C}",
  "\\angstrom": "\\text{\\AA}",
  "\\micro": "\\mu",
  "\\ohm": "\\Omega",
  "\\unit": "\\text",
  "\\R": "\\mathbb{R}",
  "\\N": "\\mathbb{N}",
  "\\Z": "\\mathbb{Z}",
  "\\Q": "\\mathbb{Q}",
  "\\C": "\\mathbb{C}",
};

/**
 * Normalizes mathematical sub-expressions and symbols (unicode powers, subscripts, fractions, operators)
 */
export const normalizeMathExpr = (str) => {
  if (!str || typeof str !== "string") return "";
  let res = str.trim();

  // Strip wrapping math delimiters if present inside sub-expression
  res = res.replace(/^\$+|\$+$/g, "").trim();

  // Clean mathrel artifacts
  res = res.replace(/\\?mathrel\s*\{[^}]*\}/gi, " \\neq ");

  // Convert unicode superscripts
  const supMap = {
    "⁰": "^0", "¹": "^1", "²": "^2", "³": "^3", "⁴": "^4",
    "⁵": "^5", "⁶": "^6", "⁷": "^7", "⁸": "^8", "⁹": "^9",
    "⁺": "^+", "⁻": "^-", "ⁿ": "^n", "ⁱ": "^i"
  };
  for (const [k, v] of Object.entries(supMap)) {
    res = res.split(k).join(v);
  }
  res = res.replace(/\^-\^1|\^-1/g, "^{-1}");
  res = res.replace(/\^-\^2|\^-2/g, "^{-2}");
  res = res.replace(/\^-\^3|\^-3/g, "^{-3}");

  // Convert unicode subscripts
  const subMap = {
    "₀": "_0", "₁": "_1", "₂": "_2", "₃": "_3", "₄": "_4",
    "₅": "_5", "₆": "_6", "₇": "_7", "₈": "_8", "₉": "_9"
  };
  for (const [k, v] of Object.entries(subMap)) {
    res = res.split(k).join(v);
  }

  // Convert not equal and inequality operators
  res = res.replace(/≠|!=/g, " \\neq ");
  res = res.replace(/\\ne\b/g, "\\neq");
  res = res.replace(/≤|<=/g, " \\le ");
  res = res.replace(/≥|>=/g, " \\ge ");
  res = res.replace(/±/g, " \\pm ");
  res = res.replace(/∓/g, " \\mp ");
  res = res.replace(/×/g, " \\times ");
  res = res.replace(/÷/g, " \\div ");
  res = res.replace(/·/g, " \\cdot ");
  res = res.replace(/≈/g, " \\approx ");
  res = res.replace(/≡/g, " \\equiv ");
  res = res.replace(/∈/g, " \\in ");
  res = res.replace(/∉/g, " \\notin ");
  res = res.replace(/∞/g, " \\infty ");

  // Trig and standard function names without leading backslash & fix merged tokens (sinleft, xright)
  res = res.replace(/(?<!\\)\b(sin|cos|tan|cot|sec|csc|log|ln|exp|det|lim)left\b/gi, "\\$1\\left");
  res = res.replace(/(?<!\\)\b(sin|cos|tan|cot|sec|csc|log|ln|exp|det|lim)\s+left\b/gi, "\\$1\\left");
  res = res.replace(/(?<!\\)\b(sin|cos|tan|cot|sec|csc|ln|log|exp|det|lim)\b(?=\s*[\(\{\[_0-9a-zA-Z]|\s*\\)/gi, "\\$1");

  // Format fractions inside trig / functions or brackets
  // e.g. \sin(1/x), \sin(1 / x), \sin\left(1/x\right), \sin(\frac{1}{x}), sinleft(1/xright) -> \sin\left(\dfrac{1}{x}\right)
  res = res.replace(
    /(?:\\|\b)(sin|cos|tan|cot|sec|csc)\s*(?:\\?left)?\s*\(\s*(?:\\?(?:d|t)?frac\{([^{}]+)\}\{([^{}]+)\}|([0-9a-zA-Z]+)\s*\/\s*([0-9a-zA-Z]+(?:\\?right)?))\s*(?:\\?right)?\s*\)/gi,
    (m, fn, n1, d1, n2, d2) => {
      const num = (n1 || n2 || "").trim();
      const rawDen = (d1 || d2 || "").trim();
      const den = rawDen.replace(/\\?right$/i, "").trim();
      return `\\${fn.toLowerCase()}\\left(\\dfrac{${num}}{${den}}\\right)`;
    }
  );

  // Standalone simple fractions like 1/x (not inside commands or plain numbers with large denominator)
  res = res.replace(/(?<=[=+\-*\/(\s]|^)([0-9a-zA-Z]+)\s*\/\s*([0-9a-zA-Z]+)(?=[=+\-*\/)\s.,;:]|$)/g, (m, num, den) => {
    if (/^[0-9]+$/.test(num) && /^[0-9]+$/.test(den) && Number(den) > 12) return m;
    return `\\dfrac{${num}}{${den}}`;
  });

  // Clean spacing & punctuation
  res = res.replace(/,+/g, ",");
  res = res.replace(/\s+/g, " ").trim();

  return res;
};

/**
 * Converts piecewise function definitions (matrix, smallmatrix, plain { ... ; ... }) to standard KaTeX \begin{cases}
 */
export const formatPiecewiseFunction = (text) => {
  if (!text || typeof text !== "string") return text;
  let clean = text;

  // 0. Repair missing backslashes on LaTeX environment keywords & commands
  clean = clean.replace(/(?<!\\)\b(begin|end)\{(cases|matrix|pmatrix|bmatrix|vmatrix|smallmatrix|array|aligned|gathered)\}/g, "\\$1{$2}");
  clean = clean.replace(/\\?mathrel\s*\{[^}]*\}/gi, " \\neq ");

  // 0.1 Strip any existing wrapping $$ or $ around piecewise expressions to prevent double-wrapped $$$$
  clean = clean.replace(/\$\$([\s\S]*?\\begin\{cases\}[\s\S]*?\\end\{cases\}[\s\S]*?)\$\$/gi, "$1");
  clean = clean.replace(/\$([^\$\n]*?\\begin\{cases\}[\s\S]*?\\end\{cases\}[^\$\n]*?)\$/gi, "$1");

  // 1. Transform existing \begin{cases}...\end{cases} to clean display block
  clean = clean.replace(
    /((?:[a-zA-Z](?:'[a-zA-Z0-9]*)?(?:\([a-zA-Z0-9,\s\-_/+\\]+\))?|[a-zA-Z])\s*=\s*)?\\begin\{cases\}([\s\S]*?)\\end\{cases\}/gi,
    (match, fnHead, body) => {
      const prefix = fnHead ? fnHead.trim() + " " : "";
      let cleanBody = body;
      cleanBody = cleanBody.replace(/(?<=[0-9a-zA-Z\s])\s+([0-9a-zA-Z]+)\s*,\s*&/g, " \\\\ $1, &");
      cleanBody = cleanBody.replace(/(?<=\b[0-9a-zA-Z]+)\s+(0\s*,\s*&)/g, " \\\\ $1");
      const rows = cleanBody.split(/\\\\/g).map((r) => r.trim()).filter(Boolean);
      const formattedRows = rows.map((r) => {
        if (r.includes("&")) {
          const [expr, ...condParts] = r.split("&");
          const cleanExpr = expr.trim().replace(/^[,\s;]+|[,\s;]+$/g, "").trim();
          return `${normalizeMathExpr(cleanExpr)}, & ${normalizeMathExpr(condParts.join("&").trim())}`;
        }
        return normalizeMathExpr(r);
      });
      return `$$${prefix}\\begin{cases} ${formattedRows.join(" \\\\ ")} \\end{cases}$$`;
    }
  );

  // 2. Transform \left\{\begin{smallmatrix/matrix/array}... to \begin{cases}...\end{cases}
  clean = clean.replace(
    /((?:[a-zA-Z](?:'[a-zA-Z0-9]*)?(?:\([a-zA-Z0-9,\s\-_/+\\]+\))?|[a-zA-Z])\s*=\s*)?\\left\\\{\s*\\begin\{(?:smallmatrix|matrix|array)\}([\s\S]*?)\\end\{(?:smallmatrix|matrix|array)\}\s*(?:\\right\.?|\.)?/gi,
    (match, fnHead, body) => {
      const prefix = fnHead ? fnHead.trim() + " " : "";
      const rows = body.split(/\\\\/g).map((r) => r.trim()).filter(Boolean);
      const formattedRows = rows.map((r) => {
        if (r.includes("&")) {
          const [expr, ...condParts] = r.split("&");
          const cleanExpr = expr.trim().replace(/^[,\s;]+|[,\s;]+$/g, "").trim();
          return `${normalizeMathExpr(cleanExpr)}, & ${normalizeMathExpr(condParts.join("&").trim())}`;
        }
        return normalizeMathExpr(r);
      });
      return `$$${prefix}\\begin{cases} ${formattedRows.join(" \\\\ ")} \\end{cases}$$`;
    }
  );

  // 3. Transform plain text / OCR piecewise definitions:
  // e.g. "f(x) = { x² sin(1/x), x ≠ 0 ; 0 , x = 0."
  // or "f(x) = { x² sin(1/x), x ≠ 0; { 0, x = 0. Then at x = 0:"
  // or "f(x) = { x^2 sin(1/x), x != 0 ; 0, x = 0 }"
  // or "f (x) = { x² sin(1/x), x ≠ 0 ; 0, x = 0 }, Then at x = 0"
  const piecewisePattern =
    /((?:[a-zA-Z](?:'[a-zA-Z0-9]*)?\s*(?:\([a-zA-Z0-9,\s\-_/+\\]+\))?|[a-zA-Z])\s*=\s*)(?:\\left\s*)?(?:\\\{|\{)\s*([\s\S]+?(?:;|\n|\\\\)[\s\S]+?)(?:\s*\\right\.?|\s*\\\}|\s*\}([,.;]?)\s*|\s*\.|\s*(?=(?:,?\s*(?:Then|when|where|for|at|\n\n|$))))/gi;

  clean = clean.replace(piecewisePattern, (match, fnHead, body, trailingPunct) => {
    const prefix = fnHead ? fnHead.trim() + " " : "";
    const suffix = trailingPunct ? trailingPunct + " " : "";
    const branches = body.split(/[;\n]|\\\\/g).map((b) => b.trim()).filter(Boolean);

    const formattedBranches = branches.map((branch) => {
      let cleanBranch = branch.replace(/^[,\s;]+|[,\s;]+$/g, "").trim();

      // Check if branch contains &
      if (cleanBranch.includes("&")) {
        const [expr, ...condParts] = cleanBranch.split("&");
        const cleanExpr = expr.replace(/^[,\s;]+|[,\s;]+$/g, "").trim();
        return `${normalizeMathExpr(cleanExpr)}, & ${normalizeMathExpr(condParts.join("&").trim())}`;
      }

      // Check if branch contains if / for / when / where keyword
      const keywordMatch = cleanBranch.match(/\b(if|for|when|where)\b/i);
      if (keywordMatch) {
        const kwIdx = keywordMatch.index;
        const kw = keywordMatch[0];
        const expr = cleanBranch.substring(0, kwIdx).trim().replace(/^[,\s;]+|[,\s;]+$/g, "");
        const cond = cleanBranch.substring(kwIdx + kw.length).trim().replace(/^[,\s;]+|[,\s;]+$/g, "");
        return `${normalizeMathExpr(expr)}, & \\text{${kw} } ${normalizeMathExpr(cond)}`;
      }

      // Split by comma
      const commaIdx = cleanBranch.indexOf(",");
      if (commaIdx !== -1) {
        const expr = cleanBranch.substring(0, commaIdx).trim().replace(/^[,\s;]+|[,\s;]+$/g, "");
        const cond = cleanBranch.substring(commaIdx + 1).trim().replace(/^[,\s;]+|[,\s;]+$/g, "");
        return `${normalizeMathExpr(expr)}, & ${normalizeMathExpr(cond)}`;
      }

      return normalizeMathExpr(cleanBranch);
    });

    return `$$${prefix}\\begin{cases} ${formattedBranches.join(" \\\\ ")} \\end{cases}$$${suffix}`;
  });

  return clean;
};

/**
 * Standardize and clean input string to fix common escape artifacts from APIs, JSON, and OCR.
 */
export const normalizeLatexString = (str) => {
  if (!str || typeof str !== "string") return "";
  let clean = str;

  // 1. Unescape literal \n sequences to real newlines (do not match LaTeX commands like \neq, \nabla, \notin, \nu, \ne)
  clean = clean.replace(/\\n(?![a-zA-Z])/g, "\n");

  // 2. Unescape escaped dollar signs (\$ -> $)
  clean = clean.replace(/\\+\$/g, "$");

  // 3. Fix JSON control character anomalies
  // eslint-disable-next-line no-control-regex
  clean = clean.replace(/\x09ext/g, "\\text");
  // eslint-disable-next-line no-control-regex
  clean = clean.replace(/\x0Dightarrow/g, "\\rightarrow");
  clean = clean.replace(/\\x09ext/g, "\\text");
  clean = clean.replace(/\\x0Dightarrow/g, "\\rightarrow");

  // 4. Normalize double backslashes in math commands (e.g. \\frac -> \frac)
  clean = clean.replace(/\\\\([a-zA-Z]+)/g, "\\$1");

  // 5. Piecewise functions & piecewise matrix formatting (\begin{cases})
  clean = formatPiecewiseFunction(clean);

  // 6. Formatting context strings like "Then at x = 0:" or "at x = 0:" into clean inline math
  clean = clean.replace(/(?:,\s*)?\b(Then(?:,|\s+)?\s*(?:at|for|when)?\s*)([a-zA-Z])\s*([=<>≤≥≠]|\\neq|\\le|\\ge)\s*([0-9a-zA-Z\-+]+)(:?)/gi, (m, p1, v, op, val, colon) => {
    const normOp = op === "≠" ? "\\neq" : op === "≤" ? "\\le" : op === "≥" ? "\\ge" : op;
    const hasLeadingComma = m.startsWith(",");
    return `${hasLeadingComma ? ", " : ""}${p1.trim()} $${v} ${normOp} ${val}$${colon || ""}`;
  });

  // 7. Format mathematical function references in options/text: e.g. "f'", "f and f'", "f is continuous"
  clean = clean.replace(/(?<![\$\\\w])f'(?![\$\w])/g, "$f'$");
  clean = clean.replace(/(?<![\$\\\w])f(?=\s+(?:is|and|both|at|continuous|differentiable|not|defined))\b/g, "$f$");

  // 8. Unwrap \textit{...} and \mathit{...} before math delimiter parsing
  clean = clean.replace(/['"‘“]?\$\s*\\(?:textit|mathit)\{([a-zA-Z0-9\-]+)\}\s*\$['"’”]?/g, "*$1*");
  clean = clean.replace(/\\(?:textit|mathit)\{([^{}]+)\}/g, "*$1*");
  clean = clean.replace(/\$\s*\*([a-zA-Z0-9]+)\*\s*\$/g, "*$1*");

  // 9. Chemical prefix descriptors like $p$-Nitrophenol, $o$-cresol, $m$-xylene -> *p*-Nitrophenol, etc.
  clean = clean.replace(/\$\s*([pomn]|cis|trans|tert|sec|iso|neo)\s*\$-/gi, "*$1*-");

  // 10. Unwrap pure text blocks inside math delimiters (e.g. $\text{...}$)
  clean = clean.replace(/\$\s*\\(?:text|mathrm|mathbf)\{([^{}\$\n]+)\}\s*\$/g, (match, inner) => {
    if (!/[\\^_]/.test(inner)) return inner;
    return match;
  });

  // 11. Standalone LaTeX text-math single letters or labels: $\text{X}$ -> X, $\text{'X'}$ -> X, '$\text{X}$' -> X, $\mathrm{Y}$ -> Y
  clean = clean.replace(/['"‘“]?\$\s*\\(?:text|mathrm|mathbf)\{['"‘“]?([A-Za-z0-9]{1,4})['"’”]?\}\s*\$['"’”]?/g, "$1");
  clean = clean.replace(/(?<![\$\\\w])\\(?:text|mathrm|mathbf)\{['"‘“]?([A-Za-z0-9]{1,4})['"’”]?\}(?![\$\w])/g, "$1");
  clean = clean.replace(/\\text\{['"‘“]([a-zA-Z0-9]+)['"’”]\}/g, "\\text{$1}");

  // 12. Strip single/double/curly quotes around isolated variable letters or chemical labels:
  // ('X' -> X, "X" -> X, ‘X’ -> X, “X” -> X, 'Y' -> Y, 'A' -> A, 'H2SO4' -> H2SO4, 'Zn/H2O' -> Zn/H2O, etc.)
  clean = clean.replace(/(?<=\b|\s|^|[(\[{])['"‘“]([a-zA-Z0-9/+\-]{1,10})['"’”](?=\b|\s|[,.:;?!)]|$)/g, "$1");

  // 13. Fix awkward inner spacing inside parentheses: ( HBr ) -> (HBr), ( $\text{HBr}$ ) -> ($\text{HBr}$)
  clean = clean.replace(/\(\s+([^\(\)\n]+?)\s+\)/g, "($1)");
  clean = clean.replace(/\(\s+([^\(\)\n]+?\))/g, "($1");
  clean = clean.replace(/(\([^\(\)\n]+?)\s+\)/g, "$1)");

  // 14. Trim whitespace strictly INSIDE $...$ and $$...$$ delimiters without touching outside spaces
  clean = clean.replace(/\$\$\s*\$\$/g, "");
  clean = clean.replace(/(?<!\S)\$\$(?!\S)/g, "");
  clean = clean.replace(/\$\$\s*([\s\S]+?)\s*\$\$/g, (m, inner) => `$$${inner.trim()}$$`);
  clean = clean.replace(/(?<!\$)\$(?!\$)\s*([^\$\n]+?)\s*(?<!\$)\$(?!\$)/g, (m, inner) => {
    const trimmed = inner.trim();
    return trimmed ? `$${trimmed}$` : "";
  });

  // 15. Convert plain-text LaTeX styling commands (\textbf, \textit) to markdown
  clean = clean.replace(/\\textbf\{([^{}]+)\}/g, "**$1**");
  clean = clean.replace(/\\textit\{([^{}]+)\}/g, "*$1*");

  // 16. Format inline lists of Roman numerals / items separated by colons and semicolons onto distinct lines
  clean = clean.replace(/:\s*((?:I|II|III|IV|V|VI|i|ii|iii|iv|v|\(i\)|\(ii\)|\(iii\)|\(iv\)|\(v\)|\(I\)|\(II\)|\(III\)|\(IV\)|\(1\)|\(2\)|\(3\)|\(4\))[.)]\s+)/gi, (match, p1) => {
    return `?\n\n${p1}`;
  });
  clean = clean.replace(/;\s*((?:II|III|IV|V|VI|ii|iii|iv|v|\(ii\)|\(iii\)|\(iv\)|\(v\)|\(II\)|\(III\)|\(IV\)|\(2\)|\(3\)|\(4\))[.)]\s+)/gi, (match, p1) => {
    return `\n${p1}`;
  });
  if (clean.includes("?\n\n")) {
    clean = clean.replace(/\?\s*$/, "");
  }

  // 17. Normalize Roman numeral & label comparison orders (e.g. "$II > I > IV > III$", "$\text{II} > \text{I} > \text{IV} > \text{III}$") to clean plain text
  clean = clean.replace(/(?<!\$)\$(?!\$)\s*([\\a-zA-Z0-9\s><=≥≤≠,;:()\/–\-\.\{\}]+)\s*(?<!\$)\$(?!\$)/g, (match, inner) => {
    const withoutText = inner.replace(/\\(?:text|mathrm|mathbf|mathit)\{[^{}]+\}/g, "").trim();
    if (/^[IVXLCDMivxlcdm\s><=≥≤≠.,;:()\/–\-]+$/i.test(withoutText) && /[><=≥≤≠]/.test(withoutText) && !/[a-hj-uw-z0-9]/i.test(withoutText)) {
      return withoutText;
    }
    const romanCheck = inner.replace(/\\(?:text|mathrm|mathbf|mathit)\{([^{}]+)\}/g, "$1").trim();
    if (/^(?:[IVXLCDMivxlcdm]+|\([ivxlcdm0-9]+\))$/i.test(romanCheck)) {
      return romanCheck;
    }
    return match;
  });

  // 18. Collapse excessive consecutive line breaks
  clean = clean.replace(/\n{3,}/g, "\n\n");
  clean = clean.trim();

  return clean;
};

/**
 * Balances unclosed curly braces in LaTeX math strings to prevent parse errors.
 */
export const balanceBraces = (str) => {
  if (!str || typeof str !== "string") return "";
  let openCount = 0;
  let result = "";

  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const isEscaped = i > 0 && str[i - 1] === "\\";

    if (char === "{" && !isEscaped) {
      openCount++;
      result += char;
    } else if (char === "}" && !isEscaped) {
      if (openCount > 0) {
        openCount--;
        result += char;
      }
    } else {
      result += char;
    }
  }

  while (openCount > 0) {
    result += "}";
    openCount--;
  }

  return result;
};

/**
 * Sanitizes math content before passing to KaTeX.
 */
export const sanitizeMathContent = (mathStr) => {
  if (!mathStr || typeof mathStr !== "string") return "";
  let clean = mathStr.trim();

  // Strip wrapping $ or $$ if passed
  clean = clean.replace(/^\$+|\$+$/g, "").trim();

  // Normalize backslashes for math commands
  clean = clean.replace(/\\\\([a-zA-Z]+)/g, "\\$1");

  // Fix escaped json control characters
  // eslint-disable-next-line no-control-regex
  clean = clean.replace(/\x09ext/g, "\\text");
  // eslint-disable-next-line no-control-regex
  clean = clean.replace(/\x0Dightarrow/g, "\\rightarrow");

  // Convert multiple underscores (e.g. fill in the blanks) or dashes inside math mode to underlines
  clean = clean.replace(/(?<!\\)(_{2,})/g, "\\underline{\\quad\\quad}");
  clean = clean.replace(/(-{4,})/g, "\\underline{\\quad\\quad}");

  // Enhance inline \frac to \dfrac so divided-by fractions render large, clear, and distinct
  clean = clean.replace(/\\frac\{/g, "\\dfrac{");

  // Ensure not-equal is standardized to \neq
  clean = clean.replace(/≠|!=|\\ne\b/g, "\\neq");

  // Balance curly braces
  clean = balanceBraces(clean);

  return clean;
};

/**
 * Generates a clean, readable text representation of LaTeX formulas as a graceful fallback.
 */
export const cleanLatexString = (str) => {
  if (!str || typeof str !== "string") return "";
  let clean = str;

  // Convert \begin{cases} ... \end{cases} to clean readable text: { expr, cond ; expr, cond }
  clean = clean.replace(/\\begin\{cases\}([\s\S]*?)\\end\{cases\}/gi, (m, body) => {
    const rows = body.split(/\\\\/g).map((r) => r.trim()).filter(Boolean);
    const cleanedRows = rows.map((r) => {
      if (r.includes("&")) {
        const [expr, ...condParts] = r.split("&");
        const cleanExpr = expr.trim().replace(/^[\s,;.{}]+|[\s,;.{}]+$/g, "").trim();
        return `${cleanLatexString(cleanExpr)}, ${cleanLatexString(condParts.join("&").trim())}`;
      }
      return cleanLatexString(r);
    });
    return `{ ${cleanedRows.join(" ; ")} }`;
  });

  // Convert \left\{\begin{smallmatrix}... to { ... }
  clean = clean.replace(/\\left\\\{\s*\\begin\{(?:smallmatrix|matrix|array)\}([\s\S]*?)\\end\{(?:smallmatrix|matrix|array)\}\s*(?:\\right\.?|\.)?/gi, (m, body) => {
    const rows = body.split(/\\\\/g).map((r) => r.trim()).filter(Boolean);
    const cleanedRows = rows.map((r) => {
      if (r.includes("&")) {
        const [expr, ...condParts] = r.split("&");
        const cleanExpr = expr.trim().replace(/^[\s,;.{}]+|[\s,;.{}]+$/g, "").trim();
        return `${cleanLatexString(cleanExpr)}, ${cleanLatexString(condParts.join("&").trim())}`;
      }
      return cleanLatexString(r);
    });
    return `{ ${cleanedRows.join(" ; ")} }`;
  });

  // Fix escaped backslashes
  clean = clean.replace(/\\\\([a-zA-Z]+)/g, "\\$1");

  // Remove math delimiters
  clean = clean.replace(/\\\[([\s\S]+?)\\\]/g, "$1");
  clean = clean.replace(/\\\(([\s\S]+?)\\\)/g, "$1");
  clean = clean.replace(/\$\$([\s\S]+?)\$\$/g, "$1");
  clean = clean.replace(/\$([^\$\n]+?)\$/g, "$1");

  // State symbols: (\ell) -> (ℓ), \ell -> ℓ
  clean = clean.replace(/\\ell/g, "ℓ");

  // Degrees: ^{\circ} or ^\circ or \degree or \circ -> °
  clean = clean.replace(/\^\{\\circ\}|\^\\circ|\\degree|\\circ/g, "°");

  // Strip single/double/curly quotes around isolated variable letters or chemical labels
  clean = clean.replace(/(?<=\b|\s|^|[(\[{])['"‘“]([a-zA-Z0-9/+\-]{1,10})['"’”](?=\b|\s|[,.:;?!)]|$)/g, "$1");

  // Fix awkward inner spacing inside parentheses: ( HBr ) -> (HBr)
  clean = clean.replace(/\(\s+([^\(\)\n]+?)\s+\)/g, "($1)");
  clean = clean.replace(/\(\s+([^\(\)\n]+?\))/g, "($1");
  clean = clean.replace(/(\([^\(\)\n]+?)\s+\)/g, "$1)");

  // Delta commands: \Delta
  clean = clean.replace(/\\Delta\s*([A-Za-z0-9])/g, "Δ$1");
  clean = clean.replace(/\\Delta\b/g, "Δ");

  // Unwrap \text{...}, \mathrm{...}, \mathbf{...}, \mathcal{...}
  for (let i = 0; i < 4; i++) {
    clean = clean.replace(/\\(text|mathrm|mathbf|mathcal|mathit|amsrm)\{([^{}]+)\}/g, "$2");
  }

  // Greek letters
  const greekMap = {
    alpha: "α", beta: "β", gamma: "γ", Gamma: "Γ",
    delta: "δ", theta: "θ", Theta: "Θ", pi: "π", Pi: "Π",
    omega: "ω", Omega: "Ω", lambda: "λ", Lambda: "Λ",
    mu: "μ", sigma: "σ", Sigma: "Σ", epsilon: "ε",
    rho: "ρ", tau: "τ", eta: "η", phi: "φ", Phi: "Φ", psi: "ψ"
  };
  for (const [k, v] of Object.entries(greekMap)) {
    clean = clean.replace(new RegExp(`\\\\${k}\\b`, "g"), v);
  }

  // Operators
  clean = clean.replace(/\\times\b/g, " × ");
  clean = clean.replace(/\\div\b/g, " ÷ ");
  clean = clean.replace(/\\pm\b/g, " ± ");
  clean = clean.replace(/\\le\b|\\leq\b/g, " ≤ ");
  clean = clean.replace(/\\ge\b|\\geq\b/g, " ≥ ");
  clean = clean.replace(/\\neq\b/g, " ≠ ");
  clean = clean.replace(/\\approx\b/g, " ≈ ");
  clean = clean.replace(/\\equiv\b/g, " ≡ ");
  clean = clean.replace(/\\rightarrow\b/g, " → ");
  clean = clean.replace(/\\leftarrow\b/g, " ← ");
  clean = clean.replace(/\\rightleftharpoons\b/g, " ⇌ ");
  clean = clean.replace(/\\infty\b/g, " ∞ ");
  clean = clean.replace(/\\cdot\b/g, "·");

  // Trig / Math functions & bracket cleanup
  clean = clean.replace(/\\(sin|cos|tan|sec|csc|cot|ln|log|lim|exp|det)\b/g, "$1");
  clean = clean.replace(/\\left\s*\(/g, "(");
  clean = clean.replace(/\\right\s*\)/g, ")");
  clean = clean.replace(/\\left\s*\[/g, "[");
  clean = clean.replace(/\\right\s*\]/g, "]");
  clean = clean.replace(/\\left\s*\\\{/g, "{");
  clean = clean.replace(/\\right\s*\\\}/g, "}");
  clean = clean.replace(/\\right\./g, "");

  // Fractions & roots
  clean = clean.replace(/\\(d|t)?frac\{([^}]+)\}\{([^}]+)\}/g, (m, d, num, den) => {
    return `${cleanLatexString(num)}/${cleanLatexString(den)}`;
  });
  clean = clean.replace(/\\sqrt\[([^\]]+)\]\{([^}]+)\}/g, "$1√($2)");
  clean = clean.replace(/\\sqrt\{([^}]+)\}/g, (m, inner) => {
    const trimmed = inner.trim();
    if (/^[0-9a-zA-Zα-ωΑ-Ω]+$/.test(trimmed)) {
      return `√${trimmed}`;
    }
    return `√(${trimmed})`;
  });

  // Subscripts & superscripts map
  const subMap = { "0": "₀", "1": "₁", "2": "₂", "3": "₃", "4": "₄", "5": "₅", "6": "₆", "7": "₇", "8": "₈", "9": "₉", "+": "₊", "-": "₋", "=": "₌", "(": "₍", ")": "₎", "a": "ₐ", "e": "ₑ", "h": "ₕ", "i": "ᵢ", "j": "ⱼ", "k": "ₖ", "l": "ₗ", "m": "ₘ", "n": "ₙ", "o": "ₒ", "p": "ₚ", "r": "ᵣ", "s": "ₛ", "t": "ₜ", "u": "ᵤ", "v": "ᵥ", "x": "ₓ" };
  const supMap = { "0": "⁰", "1": "¹", "2": "²", "3": "³", "4": "⁴", "5": "⁵", "6": "⁶", "7": "⁷", "8": "⁸", "9": "⁹", "+": "⁺", "-": "⁻", "=": "₌", "(": "⁽", ")": "⁾", "n": "ⁿ", "i": "ⁱ" };

  clean = clean.replace(/\^2\b/g, "²");
  clean = clean.replace(/\^3\b/g, "³");
  clean = clean.replace(/\^\{([^}]+)\}/g, (m, inner) => {
    if (inner === "-1") return "⁻¹";
    if (inner === "-2") return "⁻²";
    if (inner === "-3") return "⁻³";
    if (inner === "circ") return "°";
    if (inner === "2") return "²";
    if (inner === "3") return "³";
    const converted = inner.split("").map(c => supMap[c] || c).join("");
    return converted;
  });
  clean = clean.replace(/\^([0-9])/g, (m, c) => supMap[c] || `^${c}`);

  // Subscripts: _{...} or _char
  clean = clean.replace(/_\{([^}]+)\}/g, (m, inner) => {
    if (inner === "vap" || inner === "fus" || inner === "sub" || inner === "rxn" || inner === "comb" || inner === "form") {
      return inner;
    }
    const converted = inner.split("").map(c => subMap[c] || c).join("");
    return converted;
  });
  clean = clean.replace(/_([0-9])/g, (m, c) => subMap[c] || `_${c}`);

  // Superscripts: ^{...} or ^char
  clean = clean.replace(/\^\{([^}]+)\}/g, (m, inner) => {
    if (inner === "-1") return "⁻¹";
    if (inner === "-2") return "⁻²";
    if (inner === "-3") return "⁻³";
    if (inner === "circ") return "°";
    const converted = inner.split("").map(c => supMap[c] || c).join("");
    return converted;
  });
  clean = clean.replace(/\^([0-9])/g, (m, c) => supMap[c] || `^${c}`);

  // Clean any remaining LaTeX commands or backslashes
  clean = clean.replace(/\\([a-zA-Z]+)/g, "$1");
  clean = clean.replace(/\\\\/g, " ");

  // Normalize spacing around equality/inequality operators
  clean = clean.replace(/\s*([=<>≤≥≠])\s*/g, " $1 ");
  clean = clean.replace(/[ \t]+/g, " ");
  clean = clean.replace(/ *\n */g, "\n");

  return clean.trim();
};

/**
 * Checks if a string contains un-delimited LaTeX mathematical patterns
 */
export const containsLatexPattern = (str) => {
  if (!str || typeof str !== "string") return false;

  return (
    /\\(frac|dfrac|tfrac|sqrt|sum|int|iint|iiint|oint|prod|lim|alpha|beta|gamma|Gamma|delta|Delta|epsilon|zeta|eta|theta|Theta|iota|kappa|lambda|Lambda|mu|nu|xi|Xi|pi|Pi|rho|sigma|Sigma|tau|upsilon|phi|Phi|chi|psi|Psi|omega|Omega|times|div|pm|mp|le|leq|ge|geq|neq|approx|equiv|sim|cong|propto|infty|partial|nabla|cdot|circ|degree|rightarrow|leftarrow|rightleftharpoons|Rightarrow|Leftarrow|Leftrightarrow|to|mapsto|in|notin|subset|subseteq|cap|cup|forall|exists|ell|bar|overline|hat|vec|dot|ddot|tilde|land|lor|neg|vee|wedge|oplus|otimes|text|mathrm|mathbf|mathcal|mathit|begin|smallmatrix|pmatrix|bmatrix|vmatrix|matrix|cases|aligned|gathered|sin|cos|tan|cot|sec|csc|log|ln|exp|det|left|right)\b/.test(
      str
    ) ||
    /(?:\^\{[^}]+\}|_\{[^}]+\}|\$|\\\([^\)]+\\\)|\\\[[\s\S]+?\\\])/.test(str) ||
    /[²³⁴⁵⁶⁷⁸⁹⁺⁻ⁿⁱ₀₁₂₃₄₅₆₇₈₉≠≤≥±×÷·≈≡∈∉∞√]/.test(str) ||
    /(?:[a-zA-Z](?:'[a-zA-Z0-9]*)?\s*(?:\([a-zA-Z0-9,\s\-_/+\\]+\))?|[a-zA-Z])\s*=\s*(?:\\left\s*)?(?:\\\{|\{)/.test(str) ||
    /\{\s*[^{};]+;[^{};]+\}/.test(str)
  );
};

/**
 * Safely renders LaTeX string with KaTeX
 */
export const renderKatex = (mathString, { displayMode = false } = {}) => {
  const sanitized = sanitizeMathContent(mathString);
  if (!sanitized) return "";

  const katexInstance = typeof window !== "undefined" && window.katex ? window.katex : katex;

  try {
    let html = katexInstance.renderToString(sanitized, {
      displayMode,
      throwOnError: false,
      errorColor: "#cc0000",
      strict: false,
      trust: true,
      macros: KATEX_MACROS,
    });

    if (html) {
      // Replace KaTeX composite slash-on-equals structure with clean standard Unicode ≠
      html = html.replace(
        /<span class="mrel"><span class="mrel"><span class="mord"><span class="vlist-t[^>]*>[\s\S]*?<\/span><\/span><\/span><\/span><span class="mrel">=<\/span><\/span>/g,
        '<span class="mrel" style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif !important; margin: 0 0.25em;">≠</span>'
      );
      html = html.replace(
        /<span class="mrel">\s*<span class="mrel">[\s\S]*?<\/span>\s*<span class="mrel">=<\/span>\s*<\/span>/g,
        '<span class="mrel" style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif !important; margin: 0 0.25em;">≠</span>'
      );
      // Replace KaTeX private use characters with clean Unicode ≠
      html = html.replace(/[\uE000-\uF8FF]\s*=/g, '<span class="mrel" style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif !important; margin: 0 0.25em;">≠</span>');
      html = html.replace(/[\uE000-\uF8FF]/g, '<span class="mrel" style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif !important; margin: 0 0.25em;">≠</span>');
    }

    return html;
  } catch (err) {
    console.warn("KaTeX render error:", err);
    return "";
  }
};

// Regex patterns to capture un-delimited LaTeX math expressions
const latexCmd = "\\\\[a-zA-Z]+(?:\\{(?:[^{}]|\\{[^{}]*\\})*\\})*";
const scriptCmd = "(?:[0-9A-Za-z]+)?(?:[\\^_](?:\\{(?:[^{}]|\\{[^{}]*\\})*\\}|\\\\[a-zA-Z]+|[0-9a-zA-Z+\\-°ℓ]))+";
const stateSymbol = "\\((?:\\\\ell|[lgs]|aq)\\)";
const reactionArrow = "(?:\\b[0-9A-Za-z]+\\s*)?\\\\(?:rightarrow|leftarrow|rightleftharpoons|equiv)\\s*(?:[0-9A-Za-z]+)?";
const mathHead =
  "(?:\\[\\\\text\\{[\\s\\S]*?\\]|" +
  "(?:(?:\\b[A-Za-z0-9()]+\\s*[+\\-*\\/=]\\s*)+|\\b[A-Za-z]\\s*\\(?|\\[\\s*|\\(\\s*(?:\\d+\\s*)?)?" +
  "(?:" +
  latexCmd +
  "|" +
  scriptCmd +
  "|" +
  stateSymbol +
  "|" +
  reactionArrow +
  ")" +
  ")";
// Only allow spacing when followed by a math operator, LaTeX command, script, number, single-letter variable, bracket, or \text{...}
const mathFollow =
  "(?:[0-9A-Za-z()\\[\\]+\\-*\\/=.,:;~±×÷≠≤≥≡→←↔⇌°%|]|\\\\text\\{(?:[^{}]|\\{[^{}]*\\})*\\}|" +
  latexCmd +
  "|" +
  scriptCmd +
  "|" +
  stateSymbol +
  "|\\s+(?=[+\\-*\\/=.,:;~±×÷≠≤≥≡→←↔⇌°%\\\\]|[\\^_]|\\d|\\b[a-zA-Z]\\b(?!\\w)|\\\\text|\\[|\\(|\\|))*";

const formulaRegex = new RegExp(mathHead + mathFollow, "g");

/**
 * Parses a text chunk that may contain un-delimited LaTeX expressions mixed with normal words.
 */
const parseMixedTextChunk = (chunk, segments) => {
  if (!chunk) return;

  if (!containsLatexPattern(chunk)) {
    segments.push({ type: "text", content: chunk });
    return;
  }

  // Process line by line to preserve line breaks
  const lines = chunk.split("\n");

  lines.forEach((line, lIdx) => {
    if (lIdx > 0) {
      segments.push({ type: "text", content: "\n" });
    }

    if (!line) return;

    if (!containsLatexPattern(line)) {
      segments.push({ type: "text", content: line });
      return;
    }

    const trimmed = line.trim();

    // Line is a bracketed LaTeX block: e.g. [\text{Given: } ... ]
    if (trimmed.startsWith("[\\text{") && trimmed.endsWith("]")) {
      segments.push({
        type: "math",
        display: false,
        content: trimmed,
        raw: line,
      });
      return;
    }

    // Line is mixed prose with embedded un-delimited LaTeX math expressions
    formulaRegex.lastIndex = 0;
    let lastIdx = 0;
    let match;

    while ((match = formulaRegex.exec(line)) !== null) {
      if (match.index > lastIdx) {
        segments.push({
          type: "text",
          content: line.substring(lastIdx, match.index),
        });
      }

      let mathStr = match[0];
      let trailingPunct = "";
      if (/[.,;:]$/.test(mathStr) && !/\\text\{[^}]*[.,;:]\}$/.test(mathStr)) {
        trailingPunct = mathStr.slice(-1);
        mathStr = mathStr.slice(0, -1);
      }

      segments.push({
        type: "math",
        display: false,
        content: mathStr.trim(),
        raw: match[0],
      });

      if (trailingPunct) {
        segments.push({
          type: "text",
          content: trailingPunct,
        });
      }

      lastIdx = formulaRegex.lastIndex;
    }

    if (lastIdx < line.length) {
      segments.push({
        type: "text",
        content: line.substring(lastIdx),
      });
    }
  });
};

/**
 * Breaks down a text string into structured segments of plain text, inline math, and display math.
 * Handles:
 * - Block math: `$$...$$`, `\[...\]`, `\begin{env}...\end{env}`
 * - Inline math: `$..$`, `\(...\)`
 * - Clean plain text with zero false-positive math conversions
 */
export const parseLatexSegments = (input) => {
  if (!input || typeof input !== "string") return [];

  const text = normalizeLatexString(input);
  const segments = [];

  // Pass 1: Extract block math environments ($$...$$, \[...\], \begin{...}...\end{...})
  const displayEnvRegex =
    /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|((?:[a-zA-Z](?:'[a-zA-Z0-9]*)?(?:\([a-zA-Z0-9,\s\-_/+\\]+\))?|[a-zA-Z])\s*=\s*)?\\begin\{(?:equation|equation\*|align|align\*|aligned|gather|gather\*|gathered|cases|matrix|pmatrix|bmatrix|vmatrix|Vmatrix|array|split|multline)\}[\s\S]*?\\end\{(?:equation|equation\*|align|align\*|aligned|gather|gather\*|gathered|cases|matrix|pmatrix|bmatrix|vmatrix|Vmatrix|array|split|multline)\})/g;

  let lastIndex = 0;
  let match;
  const rawBlocks = [];

  while ((match = displayEnvRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      rawBlocks.push({
        type: "mixed",
        content: text.substring(lastIndex, match.index),
      });
    }

    const matchedStr = match[0];
    let mathContent = matchedStr;

    if (matchedStr.startsWith("$$") && matchedStr.endsWith("$$")) {
      mathContent = matchedStr.slice(2, -2).trim();
    } else if (matchedStr.startsWith("\\[") && matchedStr.endsWith("\\]")) {
      mathContent = matchedStr.slice(2, -2).trim();
    }

    rawBlocks.push({
      type: "math",
      display: true,
      content: mathContent,
      raw: matchedStr,
    });

    lastIndex = displayEnvRegex.lastIndex;
  }

  if (lastIndex < text.length) {
    rawBlocks.push({
      type: "mixed",
      content: text.substring(lastIndex),
    });
  }

  // Pass 2: Process inline math ($...$ or \(...\)) inside mixed blocks
  const inlineMathRegex = /(\\\([\s\S]+?\\\)|(?<!\\)\$([^\$\n]+?)(?<!\\)\$)/g;

  for (const block of rawBlocks) {
    if (block.type === "math") {
      segments.push(block);
      continue;
    }

    const content = block.content;
    let subLastIndex = 0;
    let inlineMatch;

    while ((inlineMatch = inlineMathRegex.exec(content)) !== null) {
      if (inlineMatch.index > subLastIndex) {
        const textChunk = content.substring(subLastIndex, inlineMatch.index);
        parseMixedTextChunk(textChunk, segments);
      }

      const matchStr = inlineMatch[0];
      let innerMath = matchStr;

      if (matchStr.startsWith("\\(") && matchStr.endsWith("\\)")) {
        innerMath = matchStr.slice(2, -2).trim();
      } else if (matchStr.startsWith("$") && matchStr.endsWith("$")) {
        const inner = matchStr.slice(1, -1).trim();
        // If it starts with digits and contains English words without math symbols, it is a false-positive currency pair (e.g. "$50 and pen is $10")
        if (
          /^[0-9]+(?:\.[0-9]+)?\s+[a-zA-Z\s]+$/.test(inner) &&
          !containsLatexPattern(inner) &&
          !/[=<>+*^_\\]/.test(inner)
        ) {
          parseMixedTextChunk(matchStr, segments);
          subLastIndex = inlineMathRegex.lastIndex;
          continue;
        }
        innerMath = inner;
      }

      segments.push({
        type: "math",
        display: false,
        content: innerMath,
        raw: matchStr,
      });

      subLastIndex = inlineMathRegex.lastIndex;
    }

    if (subLastIndex < content.length) {
      const remainingText = content.substring(subLastIndex);
      parseMixedTextChunk(remainingText, segments);
    }
  }

  return segments;
};

/**
 * Parses Match The Following questions into structured columns & rows
 */
export const parseMatchTheFollowing = (text) => {
  if (!text || typeof text !== "string") return null;

  // Normalize line breaks & common HTML/markdown wrappers
  const normalizedText = text
    .replace(/\r\n/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?p>/gi, "\n");

  // Helper to format header text
  const cleanHeader = (hdr, fallback) => {
    if (!hdr) return fallback;
    let cleaned = hdr
      .replace(/[*_#`~]+/g, "")
      .replace(/[-–—_]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    cleaned = cleaned
      .replace(/^[:\-–—\s.]+/, "")
      .replace(/[:\-–—\s.]+$/, "")
      .trim();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  };

  // Helper to extract items from a block of text
  // Recognizes lines or items starting with valid item labels:
  // (A)-(F), (a)-(f), (p)-(u), (P)-(U), (1)-(99), (i)-(x), (I)-(X)
  // or A., B., 1., i., I., p., etc.
  const extractItems = (block) => {
    if (!block || !block.trim()) return [];

    // Precise label pattern:
    // 1. (A), (B), (p), (q), (1), (2), (i), (ii), (I), (II)
    // 2. [A], [B], [p], [q], [1], [2], [i], [ii]
    // 3. A., B., p., q., 1., 2., i., ii., I., II.
    // 4. A), B), p), q), 1), 2), i), ii), I), II)
    const labelTokenPattern =
      "(?:\\((?:[A-Fa-f]|[p-uP-U]|[1-9][0-9]?|i{1,4}|iv|vi{0,3}|ix|x|v|I{1,4}|IV|VI{0,3}|IX|X|V)\\)|\\[(?:[A-Fa-f]|[p-uP-U]|[1-9][0-9]?|i{1,4}|iv|vi{0,3}|ix|x|v|I{1,4}|IV|VI{0,3}|IX|X|V)\\]|(?:[A-Fa-f]|[p-uP-U]|[1-9][0-9]?|i{1,4}|iv|vi{0,3}|ix|x|v|I{1,4}|IV|VI{0,3}|IX|X|V)[.)])";

    const inlineLabelRegex = new RegExp(
      `(?:^|[\\s\\n\\r]+)(?:\\*\\*)?(${labelTokenPattern})(?:\\*\\*)?\\s+`,
      "gi"
    );

    const inlineItems = [];
    let match;
    let lastIndex = 0;
    let lastLabel = "";

    inlineLabelRegex.lastIndex = 0;
    while ((match = inlineLabelRegex.exec(block)) !== null) {
      if (lastLabel) {
        let chunk = block.substring(lastIndex, match.index).trim();
        chunk = chunk.replace(/[.,;:]$/, "").trim();
        inlineItems.push({
          label: lastLabel,
          text: chunk,
        });
      }
      lastLabel = match[1];
      lastIndex = inlineLabelRegex.lastIndex;
    }
    if (lastLabel && lastIndex <= block.length) {
      let chunk = block.substring(lastIndex).trim();
      chunk = chunk.replace(/[.,;:]$/, "").trim();
      inlineItems.push({
        label: lastLabel,
        text: chunk,
      });
    }

    if (inlineItems.length >= 2) {
      return inlineItems;
    }

    // Fallback to line-by-line
    const lines = block.split(/\n+/);
    const items = [];
    const lineLabelRegex = new RegExp(
      `^\\s*(?:\\*\\*)?(${labelTokenPattern})(?:\\*\\*)?\\s*(.*)$`,
      "i"
    );

    let currentItem = null;
    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) continue;

      const lMatch = line.match(lineLabelRegex);
      if (lMatch) {
        if (currentItem) items.push(currentItem);
        currentItem = {
          label: lMatch[1].trim(),
          text: lMatch[2].trim(),
        };
      } else if (currentItem) {
        currentItem.text = currentItem.text ? `${currentItem.text} ${line}` : line;
      }
    }
    if (currentItem) items.push(currentItem);

    return items;
  };

  // 1. STRATEGY 1: Separate Column Headers (e.g. "List I:" ... "List II:" or "Column I (desc):" ... "Column II (desc):")
  // Matches Column I, List I, Column 1, Set A, etc. anywhere in text (with or without preceding newline)
  const headerRegex =
    /(?:^|\n|[.?!:]\s+|\s{2,}|\b)(?:\*\*)?(Column|List|Set|Group|Table|Section)\s*[-–—]?\s*(I{1,3}|IV|V|1|2|3|A|B|C)\b(?:\s*[\(\[][^\)\n\]]+[\)\]])?\s*(?:\*\*)?\s*[:\-–—]?/gi;

  const headerMatches = [];
  let hMatch;
  while ((hMatch = headerRegex.exec(normalizedText)) !== null) {
    headerMatches.push({
      fullMatch: hMatch[0],
      type: hMatch[1],
      index: hMatch[2],
      startIndex: hMatch.index,
      endIndex: headerRegex.lastIndex,
    });
  }

  // If we found at least 2 candidate column headers
  if (headerMatches.length >= 2) {
    for (let i = 0; i < headerMatches.length - 1; i++) {
      const h1 = headerMatches[i];
      const h2 = headerMatches[i + 1];

      const leadIn = normalizedText.slice(0, h1.startIndex).trim();
      const col1Raw = normalizedText.slice(h1.endIndex, h2.startIndex).trim();

      const nextH = headerMatches[i + 2];
      const col2Raw = normalizedText
        .slice(h2.endIndex, nextH ? nextH.startIndex : undefined)
        .trim();

      const col1Items = extractItems(col1Raw);
      const col2Items = extractItems(col2Raw);

      if (col1Items.length >= 2 && col2Items.length >= 2) {
        let cleanLeadIn = leadIn;
        if (!cleanLeadIn) cleanLeadIn = "Match the following.";
        if (cleanLeadIn && !/[.?!:]$/.test(cleanLeadIn)) cleanLeadIn += ".";

        const col1Header = cleanHeader(h1.fullMatch, "Column I");
        const col2Header = cleanHeader(h2.fullMatch, "Column II");

        const maxLen = Math.max(col1Items.length, col2Items.length);
        const rows = Array.from({ length: maxLen }, (_, idx) => ({
          col1: col1Items[idx] || null,
          col2: col2Items[idx] || null,
        }));

        return {
          leadIn: cleanLeadIn,
          col1Header,
          col2Header,
          rows,
        };
      }
    }
  }

  // 2. STRATEGY 2: Sequential items with transition point (e.g. (A)-(D) followed by (p)-(s) or (1)-(4))
  const allItems = extractItems(normalizedText);
  if (allItems.length >= 4) {
    const isAlphabet = (l) => /^[A-Ea-e]$/.test(l.replace(/[\(\).\]]/g, ""));
    const isPQRS = (l) => /^[p-tP-T]$/.test(l.replace(/[\(\).\]]/g, ""));
    const isNumeric = (l) => /^[1-9][0-9]?$/.test(l.replace(/[\(\).\]]/g, ""));
    const isRoman = (l) =>
      /^(?:i{1,4}|iv|v|vi{0,3}|ix|x)$/i.test(l.replace(/[\(\).\]]/g, ""));

    const getLabelType = (l) => {
      const clean = l.replace(/[\(\).\]\s]/g, "");
      if (isPQRS(clean)) return "pqrs";
      if (isAlphabet(clean)) return "alpha";
      if (isNumeric(clean)) return "num";
      if (isRoman(clean)) return "roman";
      return "other";
    };

    let splitIdx = -1;
    const firstType = getLabelType(allItems[0].label);

    for (let i = 1; i < allItems.length; i++) {
      const curType = getLabelType(allItems[i].label);
      if (curType !== firstType && curType !== "other") {
        splitIdx = i;
        break;
      }
      const cleanCur = allItems[i].label.replace(/[\(\).\]\s]/g, "").toUpperCase();
      const cleanFirst = allItems[0].label.replace(/[\(\).\]\s]/g, "").toUpperCase();
      if (i >= 2 && cleanCur === cleanFirst) {
        splitIdx = i;
        break;
      }
    }

    if (splitIdx >= 2 && allItems.length - splitIdx >= 2) {
      const col1Items = allItems.slice(0, splitIdx);
      const col2Items = allItems.slice(splitIdx);

      const firstLabelPos = normalizedText.indexOf(allItems[0].label);
      let leadIn =
        firstLabelPos > 0
          ? normalizedText.slice(0, firstLabelPos).trim()
          : "Match the following.";
      leadIn = leadIn.replace(/[:\-–—]+$/, "").trim();
      if (leadIn && !/[.?!:]$/.test(leadIn)) leadIn += ".";

      let col1Header = "Column I";
      let col2Header = "Column II";

      if (/List\s*I/i.test(leadIn)) {
        col1Header = "List I";
        col2Header = "List II";
      } else if (/Column\s*I/i.test(leadIn)) {
        col1Header = "Column I";
        col2Header = "Column II";
      } else if (/Set\s*A/i.test(leadIn)) {
        col1Header = "Set A";
        col2Header = "Set B";
      }

      const maxLen = Math.max(col1Items.length, col2Items.length);
      const rows = Array.from({ length: maxLen }, (_, idx) => ({
        col1: col1Items[idx] || null,
        col2: col2Items[idx] || null,
      }));

      return {
        leadIn,
        col1Header,
        col2Header,
        rows,
      };
    }
  }

  return null;
};
